/**
 * setupAxiosAuthHeader — the axios REQUEST interceptor that attaches the
 * bearer Authorization header from localStorage on EVERY outgoing request.
 *
 * THE BUG THIS CLOSES (cold-boot auth race, verified 2026-07-12):
 *   Previously the Authorization header was installed as a SIDE EFFECT of
 *   AuthenticatedApp mounting: `useEffect(() => { axios.defaults.headers
 *   .common['Authorization'] = 'Bearer ${token}'; }, [token])` (App.js).
 *   React commits CHILD effects before PARENT effects within the same
 *   commit. On a cold boot (a page reload while already logged in —
 *   token/user restored synchronously from localStorage via
 *   readStoredAuth.js, so AuthenticatedApp mounts pre-authenticated on
 *   first paint), a routed CHILD that fires a data request from its OWN
 *   mount effect (e.g. ExerciseLayout's `loadPaths()`) DISPATCHES before
 *   AuthenticatedApp's effect has had a chance to run — axios reads
 *   `defaults.headers.common` SYNCHRONOUSLY at dispatch time, so that
 *   first request went out with NO Authorization header. The backend
 *   correctly 401'd it, which either triggered a needless refresh-token
 *   rotation (burning part of the rotation budget for no reason) or, if no
 *   refresh token was available, a spurious logout — on the very first
 *   screen a returning user saw.
 *
 * THE FIX: stop deriving the header from React lifecycle timing entirely.
 * localStorage.getItem('token') is already synchronously populated BEFORE
 * React starts rendering on a cold boot (it's read once, synchronously, by
 * App.js's `useState(readStoredAuth)` initializer — but critically, this
 * interceptor doesn't even need to wait for THAT; it reads localStorage
 * directly, independent of any component mounting at all). Installing this
 * interceptor once at module-init in index.js (before `root.render`, see
 * that file) means the header is attached correctly on the FIRST request
 * ANY component ever fires, no matter which effect runs first.
 *
 * This also makes the header track login/logout for free: handleLogin
 * (App.js) writes `token` to localStorage synchronously before any
 * post-login request fires; handleLogout removes it synchronously before
 * clearing React state. localStorage is the single source of truth — no
 * separate "keep the default in sync" bookkeeping is needed anywhere else.
 * (Historical footgun, fixed 2026-07-12: setupAxiosInterceptor.js's
 * refresh-rotation path USED to also mirror the freshly-rotated token onto
 * `axios.defaults.headers.common.Authorization`. That write was NOT harmless
 * — axios flattens `defaults.headers.common` into a request's config BEFORE
 * the request-interceptor chain runs (core/Axios.js `_request`), so a
 * lingering default satisfied THIS interceptor's `hasExplicitAuth` guard and
 * SHADOWED localStorage: a token that outlived logout and could bleed across
 * users on a shared device. That mirror-write has been removed; localStorage
 * is now the sole holder of the bearer token, and this interceptor and
 * `useClassroomActivity.js` both read it directly. handleLogout (App.js)
 * also `delete`s the default as belt-and-suspenders.)
 *
 * PRECEDENCE: an explicit per-request `Authorization` header (the
 * background prefetches in App.js's post-login fan-out, and
 * triggerLoginPrewarm.js / triggerAnalyticsPrefetch.js, which all carry
 * their own captured token) always WINS — this interceptor only fills the
 * header in when the caller didn't already set one. This also means the
 * retry leg of a 401→refresh cycle (setupAxiosInterceptor.js, which sets
 * `originalRequest.headers.Authorization` to the freshly-rotated token
 * before re-issuing the request) is never double-set or clobbered back to
 * the stale token: by the time that retry passes back through this
 * interceptor, `Authorization` is already present, so this interceptor is
 * a no-op on it.
 *
 * `/auth/refresh` and `/auth/logout` are NOT special-cased here (unlike the
 * response interceptor, which must not recurse through itself). Attaching
 * a — possibly stale/expired — bearer token to those calls is harmless:
 * neither route declares an auth dependency (they authenticate off the
 * `refresh_token` in the POST body instead), so FastAPI simply never reads
 * the header on those two paths.
 */
import axios from 'axios';

export function installAxiosAuthHeaderInterceptor() {
  const id = axios.interceptors.request.use((config) => {
    const hasExplicitAuth =
      config.headers &&
      (config.headers.Authorization != null || config.headers.authorization != null);
    if (!hasExplicitAuth) {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  });
  return () => axios.interceptors.request.eject(id);
}

export default installAxiosAuthHeaderInterceptor;
